import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { ROUTE_SEGMENTS } from '../../../shared/constants/routes';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatNavList } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

interface NavItem {
    id: string;
    icon: string;
    label: string;
    route: string;
    /** Small chip rendered after the label (e.g. 'DEV' for dev-only tools). */
    badge?: string;
}

interface NavGroup {
    id: string;
    label: string;
    items: NavItem[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-sidebar',
    imports: [RouterModule, MatIcon, MatTooltipModule],
    templateUrl: './sidebar.html',
    styleUrl: './sidebar.scss',
})
export class Sidebar {
    // AB#9764: mirrors `bp.down(laptop)` in breakpoints.scss / the
    // `@include bp.down(laptop)` narrow-rail rule in sidebar.scss (1024px,
    // exclusive upper bound — see breakpoints.scss for why). Kept in sync
    // by hand since Sass values aren't importable into TS; if the `laptop`
    // breakpoint ever changes, update both.
    private static readonly NARROW_RAIL_QUERY = '(max-width: 1023.98px)';

    private readonly breakpointObserver = inject(BreakpointObserver);

    // AB#9764: below the `laptop` breakpoint, sidebar.scss auto-narrows the
    // rail unconditionally (`.sidebar:not(.collapsed)` gets the same
    // `narrow-rail` mixin as `.collapsed`), but `isCollapsed` used to be a
    // plain user-toggled signal that never learned the breakpoint had
    // fired. That made the toggle button a dead control below 1024px — it
    // rendered "Collapse sidebar" / chevron_left while the rail was already
    // narrow, and clicking it changed nothing. Tracking the same media
    // query here keeps `isCollapsed()` in agreement with what's actually
    // rendered, and (combined with AB#9761's
    // `[matTooltipDisabled]="!isCollapsed()"` on nav items) correctly
    // re-enables the item tooltips on the auto-narrow rail, where the
    // `.nav-text` labels are opacity-0 and would otherwise be
    // unidentifiable by any means.
    private readonly isBelowLaptopBreakpoint = toSignal(
        this.breakpointObserver
            .observe(Sidebar.NARROW_RAIL_QUERY)
            .pipe(map((state) => state.matches)),
        {
            initialValue: this.breakpointObserver.isMatched(
                Sidebar.NARROW_RAIL_QUERY,
            ),
        },
    );

    /** True while the manual toggle has collapsed the rail — independent of
     * the breakpoint-driven auto-narrow below. */
    private readonly manuallyCollapsed = signal(false);

    /** True while the rail is actually rendered narrow: either the user
     * toggled it, or the viewport is below the `laptop` breakpoint and
     * sidebar.scss is auto-narrowing it regardless of the toggle. */
    isCollapsed = computed(
        () => this.manuallyCollapsed() || this.isBelowLaptopBreakpoint(),
    );

    /** The toggle has nothing to do below the breakpoint — the rail is
     * already forced narrow by CSS and cannot expand at this width, so the
     * control is disabled rather than left dead/misleading. */
    isToggleDisabled = computed(() => this.isBelowLaptopBreakpoint());

    // ai-dev-theme: TailAdmin-style sectioned nav — uppercase group headers
    // over pill items. Dev-only tooling sits in its own trailing section and
    // carries a DEV badge so it can't be mistaken for a product screen.
    navGroups: NavGroup[] = [
        {
            id: 'monitor',
            label: 'Monitor',
            items: [
                {
                    id: 'dashboard',
                    icon: 'dashboard',
                    label: 'Dashboard',
                    route: ROUTE_SEGMENTS.dashboard,
                },
                {
                    id: 'bet-ticker',
                    icon: 'event',
                    label: 'Bet Ticker',
                    route: ROUTE_SEGMENTS.betTicker,
                },
                {
                    id: 'bet-history',
                    icon: 'history',
                    label: 'Bet History',
                    route: ROUTE_SEGMENTS.betHistory,
                },
                {
                    id: 'liability',
                    icon: 'visibility',
                    label: 'Liability viewer',
                    route: ROUTE_SEGMENTS.liabilityViewer,
                },
            ],
        },
        {
            id: 'rules',
            label: 'Rules',
            items: [
                {
                    id: 'risk-rules',
                    icon: 'balance',
                    label: 'Risk Rules',
                    route: `${ROUTE_SEGMENTS.riskRuleList}/GeneralRules`,
                },
                {
                    id: 'absolute-rules',
                    icon: 'gavel',
                    label: 'Absolute Rules',
                    route: `${ROUTE_SEGMENTS.riskRuleList}/AbsoluteRules`,
                },
            ],
        },
        {
            id: 'groups',
            label: 'Groups',
            items: [
                {
                    id: 'entity-group',
                    icon: 'group',
                    label: 'Entity group',
                    route: `${ROUTE_SEGMENTS.groupList}/EntityGroups`,
                },
                {
                    id: 'content-group',
                    icon: 'layers',
                    label: 'Content group',
                    route: `${ROUTE_SEGMENTS.groupList}/ContentGroups`,
                },
                {
                    id: 'market-type',
                    icon: 'flag',
                    label: 'Market type group',
                    route: `${ROUTE_SEGMENTS.groupList}/MarketTypeGroups`,
                },
                {
                    id: 'rule-range',
                    icon: 'height',
                    label: 'Rule range group',
                    route: `${ROUTE_SEGMENTS.groupList}/RuleRangeGroups`,
                },
            ],
        },
        {
            id: 'dev-tools',
            label: 'Dev tools',
            items: [
                {
                    id: 'betslip-pusher',
                    icon: 'send',
                    label: 'Betslip pusher',
                    route: ROUTE_SEGMENTS.betslipPusher,
                    badge: 'DEV',
                },
            ],
        },
    ];

    toggleSidebar() {
        // Below the breakpoint the rail is already forced narrow by CSS —
        // there is nothing for a manual toggle to do (the template also
        // disables the control there, this is belt-and-suspenders against
        // any other caller).
        if (this.isBelowLaptopBreakpoint()) {
            return;
        }
        this.manuallyCollapsed.update((collapsed) => !collapsed);
    }
}
